import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { videosApi } from "@/api/videos.ts";
import { creditsApi } from "@/api/credits.ts";
import { presetsApi } from "@/api/presets.ts";
import { useAuth } from "@/hooks/useAuth.ts";
import {
  PageContainer, SectionHeader, StatCard, TfCard, EmptyState,
  Btn, Icon, StatusPill, FormatBadge, VideoThumb, TfSpinner,
} from "@/components/v2/primitives.tsx";
import type { Video } from "@vms/tubeforge-shared";

function videoStageRoute(v: Video): string {
  const s = v.status;
  if (s === "script_generating" || s === "script_ready") return `/videos/${v.id}/script`;
  if (s === "script_approved" || s === "storyboard_generating" || s === "storyboard_ready") return `/videos/${v.id}/storyboard`;
  if (["storyboard_approved","generating_clips","voiceover_generating","rendering"].includes(s)) return `/videos/${v.id}/render`;
  return `/videos/${v.id}`;
}

function isInProgress(status: string): boolean {
  return ["script_generating","script_ready","script_approved","storyboard_generating","storyboard_ready","storyboard_approved","generating_clips","voiceover_generating","rendering"].includes(status);
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: videosData, isLoading } = useQuery({
    queryKey: ["videos", { limit: 20 }],
    queryFn: () => videosApi.list({ limit: 20 }),
  });

  const { data: usageData } = useQuery({
    queryKey: ["credits", "usage"],
    queryFn: () => creditsApi.usage(),
  });

  const videos = videosData?.videos ?? [];
  const inProgress = videos.filter(v => isInProgress(v.status)).slice(0, 3);
  const published = videos.filter(v => v.status === "published" || v.status === "complete").slice(0, 4);
  const hasNoVideos = !isLoading && videos.length === 0;

  const { data: presetsData } = useQuery({
    queryKey: ["presets"],
    queryFn: () => presetsApi.list(),
    enabled: hasNoVideos,
  });

  const videoCredits = usageData?.videoCredits ?? 0;
  const userName = user?.email?.split("@")[0] ?? "there";

  if (hasNoVideos) {
    return (
      <PageContainer title="Dashboard" subtitle={`Welcome, ${userName}. Let's make your first video.`}>
        <TfCard padding={32} style={{ textAlign: "center", marginBottom: 32 }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
            Welcome! Let's make your first video
          </h2>
          <p className="tf-muted" style={{ fontSize: 14, marginTop: 8, maxWidth: 480, marginInline: "auto" }}>
            Pick a niche below, review the AI-generated script, approve the storyboard, and get a publish-ready MP4 in minutes.
            You have {videoCredits} free video{videoCredits === 1 ? "" : "s"} to try.
          </p>
        </TfCard>

        <div className="tf-grid-3col" style={{ marginBottom: 32 }}>
          {[
            { icon: "sparkle", title: "1. Generate script", desc: "AI writes a script in your niche. Edit, tweak the tone, or regenerate." },
            { icon: "grid", title: "2. Approve storyboard", desc: "AI creates an image for each scene. Swap any you don't like." },
            { icon: "play", title: "3. Get your video", desc: "Voiceover, captions, and music are added automatically." },
          ].map(step => (
            <TfCard key={step.title} padding={20}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--tf-accent-soft)", color: "var(--tf-accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={step.icon} size={18} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{step.title}</div>
                  <p className="tf-muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{step.desc}</p>
                </div>
              </div>
            </TfCard>
          ))}
        </div>

        <SectionHeader title="Pick your niche" />
        {presetsData ? (
          <div className="tf-grid-2col">
            {presetsData.presets.map(preset => (
              <Link key={preset.id} to={`/videos/new?preset=${preset.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                <TfCard padding={16} style={{ cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{preset.displayName}</div>
                      <p className="tf-muted" style={{ fontSize: 13, marginTop: 4 }}>{preset.description}</p>
                    </div>
                    <FormatBadge format={preset.format} />
                  </div>
                </TfCard>
              </Link>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><TfSpinner /></div>
        )}
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Btn size="lg" icon="plus" onClick={() => navigate("/videos/new")}>Or start from scratch</Btn>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Dashboard"
      subtitle={`Welcome back, ${userName}. Here's what's cooking.`}
      action={<Btn icon="plus" size="lg" onClick={() => navigate("/videos/new")}>New video</Btn>}
    >
      <SectionHeader title="In progress" subtitle="Pick up where you left off"
        action={inProgress.length > 0 ? <Btn variant="ghost" size="sm" onClick={() => navigate("/videos")}>See all</Btn> : undefined}
      />
      {inProgress.length === 0 ? (
        <TfCard style={{ marginBottom: 32 }}>
          <EmptyState icon="video" title="Nothing in progress"
            sub="Start a new video and we'll surface it here so you can jump back in any time."
            action={<Btn icon="plus" onClick={() => navigate("/videos/new")}>Create your first video</Btn>}
          />
        </TfCard>
      ) : (
        <div className="tf-grid-cards" style={{ marginBottom: 32 }}>
          {inProgress.map(v => {
            const ctaLabel = v.status.includes("script") ? "Review script" : v.status.includes("storyboard") ? "Review storyboard" : "View progress";
            const ctaIcon = v.status.includes("script") ? "edit" : v.status.includes("storyboard") ? "grid" : "play";
            return (
              <TfCard key={v.id} padding={0} style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ position: "relative" }}>
                  <VideoThumb title={v.scriptText?.split("\n")[0]?.slice(0, 80) ?? "Untitled"} format={v.format}
                    thumbnailUrl={v.selectedThumbnailUrl || v.thumbnailUrls?.[0]} height={150} showTitle={false} />
                  <div style={{ position: "absolute", top: 10, left: 10 }}><StatusPill status={v.status} /></div>
                </div>
                <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>
                    {v.scriptText?.split("\n")[0]?.slice(0, 80) ?? "Untitled video"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <FormatBadge format={v.format} />
                    <span className="tf-muted" style={{ fontSize: 11 }}>· {new Date(v.createdAt).toLocaleDateString()}</span>
                  </div>
                  <Btn size="sm" icon={ctaIcon} onClick={() => navigate(videoStageRoute(v))} style={{ marginTop: "auto" }}>{ctaLabel}</Btn>
                </div>
              </TfCard>
            );
          })}
        </div>
      )}

      {usageData && (
        <>
          <SectionHeader title="This month" />
          <div className="tf-grid-stats" style={{ marginBottom: 32 }}>
            <StatCard label="Videos this month" value={usageData.videosThisPeriod} sub={`of ${usageData.videosLimit}`} />
            <StatCard label="Credits remaining" value={usageData.videoCredits} sub={`${usageData.planName} plan`} />
            <StatCard label="Videos remaining" value={usageData.videosRemaining} />
          </div>
        </>
      )}

      {published.length > 0 && (
        <>
          <SectionHeader title="Recently finished"
            action={<Btn variant="ghost" size="sm" onClick={() => navigate("/videos")}>View library →</Btn>} />
          <div className="tf-grid-thumbs" style={{ marginBottom: 32 }}>
            {published.map(v => (
              <button key={v.id} onClick={() => navigate(`/videos/${v.id}`)}
                style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer", textAlign: "left", font: "inherit", color: "inherit" }}>
                <VideoThumb title={v.scriptText?.split("\n")[0]?.slice(0, 80) ?? "Untitled"} format={v.format}
                  thumbnailUrl={v.selectedThumbnailUrl || v.thumbnailUrls?.[0]} height={140} />
                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>
                  {v.scriptText?.split("\n")[0]?.slice(0, 80) ?? "Untitled video"}
                </div>
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 10 }}>
                  <StatusPill status={v.status} size="sm" />
                  <span className="tf-muted" style={{ fontSize: 11 }}>{new Date(v.createdAt).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="tf-grid-2col">
        <TfCard style={{ background: "linear-gradient(135deg, var(--tf-accent-soft), transparent)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, background: "var(--tf-accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="sparkle" size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Tip · Batch your scripts</h3>
              <p className="tf-muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                Approve 3-5 scripts in a row before starting storyboards — your generation queue stays warm.
              </p>
            </div>
          </div>
        </TfCard>
        <TfCard>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, background: "var(--tf-surface)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="youtube" size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Connect a YouTube channel</h3>
              <p className="tf-muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                Publish directly to YouTube once your video is ready.
              </p>
              <Btn variant="ghost" size="sm" icon="plus" style={{ marginTop: 10 }} onClick={() => navigate("/settings")}>Connect YouTube</Btn>
            </div>
          </div>
        </TfCard>
      </div>
    </PageContainer>
  );
}
